import {
  Analytics,
  Hotel as HotelModel,
  FPError,
  Suggest as SuggestModel,
  Order,
  HotelSearchCriteria,
  HotelDetailsML,
  Currency,
  User,
  HotelOffers,
  FlightSearch,
  SkyscannerPlaceType,
  FlightCore,
  Flight,
  Filter,
} from 'types';
import { Names } from './names';
import { NetworkHelper, BaseUrls, SelectedHotelPersistenceData } from 'api';
import { parse, format } from 'date-fns';
import { readQuery } from './../utils';
import { OfferUseCase } from '@/use-case';

declare var analytics: any;
export namespace Events {
  // Authorization

  const track = (name, props, needUTM?: boolean) => {
    if (needUTM) {
      props = { ...props, ...NetworkHelper.getUTMParams() };
    }

    try {
      analytics.ready(() => {
        analytics.track(name, props);
      });
      // FacebookEvents.logEvent(name, props);
      // GoogleEvents.logEvent(name, props);
    } catch (e) {}
  };

  export const getPlatform = () => {
    const env = BaseUrls.getEnv();
    const platform = localStorage.getItem('view')
      ? localStorage.getItem('view')
      : 'web';
    return `${platform}-${env}`;
  };

  export const updateTraits = (traits: Analytics.Traits, force?: boolean) => {
    try {
      const storedTraits = getTraits();
      if (storedTraits.user_id !== traits.user_id || force) {
        analytics.ready(() => {
          analytics.identify(traits.user_id, traits);
        });
      }
    } catch (e) {}
  };

  export const getTraits = (checkAnon?: boolean, withoutSub?: boolean) => {
    try {
      const user = analytics.user();
      const traits = user.traits();
      traits['session_id'] = NetworkHelper.getAuthCredentials()?.sessionId;
      if (checkAnon) {
        traits['anonymous'] = traits['user_id'] ? false : true;
      }
      traits['locale'] = NetworkHelper.getLocale();
      traits['currency'] = NetworkHelper.getCurrency().currency;
      traits['view'] = getPlatform();
      traits['version'] = process.env.VERSION;
      return traits ? traits : {};
    } catch (e) {
      return {
        view: getPlatform(),
        session_id: NetworkHelper.getAuthCredentials()?.sessionId,
      };
    }
  };
  export namespace Auth {
    export const signUpStart = () => {
      track(Names.Auth.SignUpStart, getTraits());
    };

    export const signInStart = () => {
      track(Names.Auth.SignInStart, getTraits());
    };

    export const binCheck = (bin8: string, status: 'success' | 'error') => {
      const props = {
        ...getTraits(),
        bin8,
        status,
      };
      track(Names.Auth.BinCheck, props);
    };

    export const authLink = payload => {
      const props = {
        ...getTraits(),
        payload,
      };
      track(Names.Integration.AuthLink, props);
    };

    export const applyForm = (email: string) => {
      const props = {
        ...getTraits(),
        email,
      };
      track(Names.Auth.ApplyForm, props);
    };

    export const checkCodeStart = (email: string) => {
      const props = {
        ...getTraits(),
        email,
      };
      track(Names.Auth.CheckCodeStart, props);
    };

    export const checkCodeFinish = (
      startTime: number,
      email: string,
      status: 'success' | 'error',
    ) => {
      const props = {
        ...getTraits(),
        duration: (new Date().getTime() - startTime).millisecondsToSec(),
        email,
        status,
      };
      track(Names.Auth.CheckCodeFinish, props);
    };

    export const logout = () => {
      track(Names.Auth.Logout, getTraits());
      analytics.reset();
    };
  }

  export namespace Hotel {
    export namespace Suggest {
      export const start = (text: string) => {
        const props = {
          ...getTraits(),
          text,
        };

        track(Names.Hotel.Suggest.Start, props);
      };

      export const finish = payload => {
        const props = {
          ...getTraits(),
          ...payload,
        };

        track(Names.Hotel.Suggest.Finish, props);
      };

      export const select = (suggest: Object) => {
        const props = {
          ...getTraits(),
          ...suggest,
        };
        track(Names.Hotel.Suggest.Select, props);
      };
    }

    export namespace Search {
      export const start = (
        criteria: HotelSearchCriteria,
        suggest?: SuggestModel.HotelSuggest,
      ) => {
        const props = {
          ...getTraits(),
          checkin: criteria.checkin,
          checkout: criteria.checkout,
          adults: criteria.adults,
          booking_window: criteria.getBookingWindow(),
          stay_nights: criteria.getNights(),
          default_search: criteria.isDefaultDates(),
        };

        if (suggest) {
          props['destination'] = suggest.title;
          props['type'] = suggest.type;

          if (suggest.geo) {
            props['country'] = suggest.geo.country;
          } else {
            const country = readQuery('country');
            if (country) {
              props['country'] = country;
            }
          }
        }

        track(Names.Hotel.Search.Start, props);
      };

      export const finish = (args: Object) => {
        const props = {
          ...getTraits(),
          ...args,
        };
        track(Names.Hotel.Search.Finish, props);
      };

      export const hotelCategorySelection = (args: {
        destination: string;
        hotel_class: Filter.StarsType;
      }) => {
        const props = {
          ...getTraits(),
          ...args,
        };
        track(Names.Hotel.Search.HotelCategorySelection, props);
      };
    }
    export namespace Offer {
      export const selectHotel = props => {
        props = {
          ...getTraits(),
          ...props,
        };

        track(Names.Hotel.Offer.SelectHotel, props);
      };

      export const offersStart = (
        criteria: HotelSearchCriteria,
        suggest: SuggestModel.Suggest<SuggestModel.HotelSuggest>,
      ) => {
        const props = {
          ...getTraits(true),
          hotel_id: criteria.id,
          checkin: criteria.checkin + '',
          checkout: criteria.checkout + '',
          adults: criteria.adults,
          booking_window: criteria.getBookingWindow(),
          stay_nights: criteria.getNights(),
        };

        if (criteria.searchId) {
          props['search_id'] = criteria.searchId;
        }

        if (suggest) {
          props['destination_type'] = suggest.source?.type;
          props['destination'] = suggest.title;
          if (suggest.source?.geo) {
            props['country'] = suggest.source?.geo?.country;
            props['city'] = suggest.source?.geo?.city;
            props['state'] = suggest.source?.geo?.state;
          }
        }

        track(Names.Hotel.Offer.OffersStart, props);
      };

      export const offersFinish = ({
        requestId,
        criteria,
        hotel,
        startTime,
        offers,
        error,
      }: {
        requestId: string;
        criteria: HotelSearchCriteria;
        hotel: HotelDetailsML.Details;
        startTime: number;
        offers: HotelOffers.Group[];
        error?: FPError;
      }) => {
        const allOffers = ([] as HotelOffers.Offer[]).concat(
          ...offers.map(it => it.offers),
        );
        const cheepestOfferPrices =
          allOffers && allOffers.length > 0
            ? allOffers.min(it => it.price.effectivePrice)
            : null;

        const minPubprice =
          allOffers && allOffers.length > 0
            ? allOffers.min(it => it.price.strikethrough)?.price?.strikethrough
            : 0;

        const location = hotel?.location;

        const offerFormatted =
          OfferUseCase.offerFormattedForAnalytics(cheepestOfferPrices);

        const props = {
          ...getTraits(true),
          request_id: requestId,
          hotel_id: criteria.id,
          hotel_name: hotel?.name,
          checkin: criteria.checkin + '',
          checkout: criteria.checkout + '',
          adults: criteria.adults,
          booking_window: criteria.getBookingWindow(),
          stay_nights: criteria.getNights(),
          offers: offers.length,
          hotel_min_price: offerFormatted?.price,
          error: false,
          duration: (new Date().getTime() - startTime).millisecondsToSec(),
          lhv_min_pubprice: minPubprice?.roundCent(),
          lhv_min_price: offerFormatted?.price,
          lhv_cashback: offerFormatted?.cashback,
          lhv_savings: offerFormatted?.savings,
          lhv_savings_percent: offerFormatted?.savings_percent,
        };

        if (location) {
          props['hotel_city'] = location.city;
          props['hotel_country_code'] = location.country;
        }

        if (error) {
          props['error'] = true;
          props['error_text'] = error.message;
          props['error_header'] = error.header;
        }

        const preview = SelectedHotelPersistenceData.getPersistanceById(
          hotel.id,
        );
        if (preview?.hotel && cheepestOfferPrices) {
          const previewPrices = preview.hotel.price;
          const cheepestPrices = cheepestOfferPrices.price;

          function getMismatchPercentage(
            priceBySearch: number,
            priceByOffers: number,
          ) {
            return (priceBySearch / priceByOffers) * 100 - 100;
          }

          function isPriceMismatch(
            priceBySearch: number,
            priceByOffers: number,
          ) {
            const percentage = getMismatchPercentage(
              priceBySearch,
              priceByOffers,
            );
            return Math.abs(percentage) > 1;
          }

          props['price_missmatch'] = isPriceMismatch(
            previewPrices.effectivePrice,
            cheepestPrices.effectivePrice,
          );
          props['strikethrough_diff'] = getMismatchPercentage(
            previewPrices.strikethrough,
            cheepestPrices.strikethrough,
          )?.toFixed(2);
          props['price_diff'] = getMismatchPercentage(
            previewPrices.effectivePrice,
            cheepestPrices.effectivePrice,
          )?.toFixed(2);
          props['cashback_diff'] = getMismatchPercentage(
            previewPrices.save + previewPrices.cashback,
            cheepestPrices.save + cheepestPrices.cashback,
          )?.toFixed(2);
        }

        track(Names.Hotel.Offer.OffersFinish, props);
      };

      export const selectOffer = ({
        hotel,
        offer,
        criteria,
        currency,
      }: {
        offer: HotelOffers.Offer;
        hotel: HotelDetailsML.Details;
        criteria: HotelSearchCriteria;
        currency: Currency.CURRENCIES;
      }) => {
        const offerFormatted = OfferUseCase.offerFormattedForAnalytics(offer);
        const props = {
          ...getTraits(),
          hotel_id: hotel.id,
          hotel_name: hotel.name,
          room_group: offer.originalName,
          free_cancellation: offer.flags.includes(
            HotelOffers.FilterKeys.FreeCancellation,
          ),
          eligible_for_points: offer.flags.some(
            it => it === HotelOffers.FilterKeys.EligibleForPoints,
          ),
          pay_now: offer.flags.includes(HotelOffers.FilterKeys.PayNow),
          supplier: offer.price.provider,
          lhc_stars: hotel?.rating?.stars ?? 0,
          lhc_checkin: criteria?.checkin ?? '',
          lhc_checkout: criteria?.checkout ?? '',
          lhc_stays: criteria?.getNights() ?? '',
          lhc_guests: criteria.adults ?? 0,
          lhc_window: Math.round(
            (parse(criteria.checkin).getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          lhc_country: hotel.location.country,
          lhc_location: [hotel.location.city, hotel.location.countryName]
            .filter(it => !!it)
            .join(', '),
          lhc_pubprice: offerFormatted?.pubprice,
          lhc_price: offerFormatted?.price,
          lhc_currency: currency,
          lhc_cashback: offerFormatted?.cashback,
          lhc_savings: offerFormatted?.savings,
          lhc_savings_percent: offerFormatted?.savings_percent,
        };
        track(Names.Hotel.Offer.SelectOffer, props);
      };

      export const guestAdded = () => {
        track(Names.Hotel.Offer.GuestAdded, getTraits());
      };

      export const phoneAdded = () => {
        track(Names.Hotel.Offer.PhoneAdded, getTraits());
      };

      export const cardAdded = () => {
        track(Names.Hotel.Offer.CardAdded, getTraits());
      };
    }

    export namespace Booking {
      export const start = ({
        hotel,
        offer,
        criteria,
      }: {
        hotel: HotelDetailsML.Details;
        offer: HotelOffers.Offer;
        criteria: HotelSearchCriteria;
      }) => {
        const price = offer.price;
        const offerFormatted = OfferUseCase.offerFormattedForAnalytics(offer);
        const props = {
          ...getTraits(),
          token: price._token,
          hotel_id: hotel.id,
          hotel_name: hotel.name,
          room_group: offer.originalName,
          eligible_for_points: offer.flags.some(
            it => it === HotelOffers.FilterKeys.EligibleForPoints,
          ),
          pay_now: offer.flags.includes(HotelOffers.FilterKeys.PayNow),
          free_cancelation: offer.flags.includes(
            HotelOffers.FilterKeys.FreeCancellation,
          ),
          checkin: criteria.checkin + '',
          checkout: criteria.checkout + '',
          adults: criteria.adults,
          booking_window: criteria.getBookingWindow(),
          stay_nights: criteria.getNights(),
          supplier: price.provider,
          price_public: price.price?.roundCent(),
          price_public_total: (price.price * criteria.getNights())?.roundCent(),
          taxes: price.taxes?.formatCent(),
          fees: (price.taxes - price.taxes)?.formatCent(),
          savings_total: price.save?.formatCent(),
          savings_type: price.cashback > 0 ? 'cashback' : 'discount',
          lhb_stars: hotel?.rating?.stars ?? '',
          lhb_pubprice: offerFormatted?.pubprice,
          lhb_price: offerFormatted?.price,
          lhb_cashback: offerFormatted?.cashback,
          lhb_savings: offerFormatted?.savings,
          lhb_savings_percent: offerFormatted?.savings_percent,
        };

        if (hotel.location) {
          if (hotel.location.city) props['city'] = hotel.location.city;
          if (hotel.location.state) props['state'] = hotel.location.state;
          if (hotel.location.country) props['country'] = hotel.location.country;
        }

        track(Names.Hotel.Booking.Start, props);
      };

      export const finish = ({
        hotel,
        offer,
        criteria,
        startTime,
        response,
        fpError,
      }: {
        hotel: HotelDetailsML.Details;
        offer?: HotelOffers.Offer;
        criteria: HotelSearchCriteria;
        startTime: number;
        response?: Order.Ml.Response.Booking;
        fpError?: FPError;
      }) => {
        const price = offer.price;

        const props = {
          ...getTraits(),
          token: price._token,
          hotel_id: hotel.id,
          hotel_name: hotel.name,
          room_group: offer.originalName,
          eligible_for_points: offer.flags.includes(
            HotelOffers.FilterKeys.EligibleForPoints,
          ),
          pay_now: offer.flags.includes(HotelOffers.FilterKeys.PayNow),
          free_cancelation: offer.flags.includes(
            HotelOffers.FilterKeys.FreeCancellation,
          ),
          checkin: criteria.checkin + '',
          checkout: criteria.checkout + '',
          adults: criteria.adults,
          booking_window: criteria.getBookingWindow(),
          stay_nights: criteria.getNights(),
          supplier: offer.price.provider,
          price_public: price.price?.formatCent(),
          price_public_total: (
            price.price * criteria.getNights()
          )?.formatCent(),
          taxes: price.taxes?.formatCent(),
          fees: price.taxes?.formatCent(),
          savings_total: price.save?.formatCent(),
          savings_type: price.cashback > 0 ? 'cashback' : 'discount',
          duration: (new Date().getTime() - startTime).millisecondsToSec(),
          error: false,
        };

        if (response) {
          props['booking_confirmation'] = response.order.externalId;
          props['booking_status'] = response.order.status;
          props['booking_supplier'] = response.order.provider;
        }

        if (hotel.location) {
          if (hotel.location.city) props['city'] = hotel.location.city;
          if (hotel.location.state) props['state'] = hotel.location.state;
          if (hotel.location.country) props['country'] = hotel.location.country;
        }

        if (fpError) {
          props.error = true;
          props['error_text'] = fpError.message;
          props['error_header'] = fpError.header;
        }

        track(Names.Hotel.Booking.Finish, props);
      };

      export namespace PopUp {
        export const start = (supplier: string) => {
          const props = {
            ...getTraits(),
            supplier,
          };

          track(Names.Hotel.Booking.PopUpStart, props);
        };

        export const continuePopUp = (supplier: string) => {
          const props = {
            ...getTraits(),
            supplier,
          };

          track(Names.Hotel.Booking.PopUpContinue, props);
        };
      }

      export namespace ThreeDS {
        export const start = () => {
          track(Names.Hotel.Booking.ThreeDS.Start, getTraits());
        };

        export const finish = () => {
          track(Names.Hotel.Booking.ThreeDS.Finish, getTraits());
        };

        export const show = () => {
          track(Names.Hotel.Booking.ThreeDS.Show, getTraits());
        };
      }

      export const startCvvVerification = () => {
        track(Names.Hotel.Booking.StartCvvVerification, getTraits());
      };
    }

    export namespace Price {
      export const check = ({
        hotelId,
        rooms,
      }: {
        hotelId: string;
        rooms: HotelModel.Group[];
      }) => {
        // const preview =
        //   SelectedHotelPersistenceData.getPersistanceById(hotelId);
        // if (!preview || !preview.hotel || !preview.searchUrl) {
        //   return;
        // }
        // const { price, name } = preview.hotel;
        // const allOffers = ([] as HotelModel.OfferV2[]).concat(
        //   ...rooms.map(it => it.offers),
        // );
        // const cheapestOffer = (allOffers ?? []).min(
        //   it => it.prices.disable_credits.price_with_extra_savings_with_taxes,
        // );
        // const props = {
        //   ...getTraits(),
        //   hotel_id: hotelId,
        //   hotel_name: name,
        //   search_url: preview.searchUrl,
        //   search_price: price?.effectivePrice,
        //   search_savings: price?.save + price?.cashback,
        // };
        // if (cheapestOffer) {
        //   const { disable_credits } = cheapestOffer.prices;
        //   const minOfferPrice =
        //     disable_credits.price_with_extra_savings_with_taxes / 100;
        //   const minOfferSavings =
        //     (disable_credits.public_price_with_taxes -
        //       disable_credits.price_with_extra_savings_with_taxes) /
        //     100;
        //   const priceDiff = (minOfferPrice - price.effectivePrice).toFixed(2);
        //   const savingsDiff = (minOfferSavings - price.save).toFixed(2);
        //   props['best_offer_price'] = minOfferPrice;
        //   props['best_offer_savings'] = minOfferSavings;
        //   props['currency'] = price.currency;
        //   props['price_diff'] = priceDiff;
        //   props['savings_diff'] = savingsDiff;
        // }
        // track(Names.Hotel.Price.Check, props);
      };
    }
  }

  export namespace Car {
    export namespace Search {
      export const start = () => {
        track(Names.Car.Search.Start, getTraits());
      };

      export const finish = () => {
        track(Names.Car.Search.Finish, getTraits());
      };
    }

    export namespace Offer {
      export const select = () => {
        track(Names.Car.Offer.Select, getTraits());
      };
    }

    export namespace Redirect {
      export const show = () => {
        track(Names.Car.Redirect.Show, getTraits());
      };

      export const continueRedirect = () => {
        track(Names.Car.Redirect.Continue, getTraits());
      };
    }
  }

  export namespace Flights {
    export namespace Search {
      export const start = (args: Object) => {
        const props = {
          ...getTraits(),
          ...args,
        };

        track(Names.Flights.Search.Start, props);
      };

      export const finish = (args: Object) => {
        const props = {
          ...getTraits(),
          ...args,
        };

        track(Names.Flights.Search.Finish, props);
      };
    }

    export namespace Offer {
      export const select = (props: {
        offer: FlightSearch.Itineraries;
        currency: Currency.CURRENCIES;
        criteria: Flight.SearchCriteria;
      }) => {
        track(Names.Flights.Offer.Select, getOfferSelectData(props));
      };
    }

    export module Redirect {
      export const show = (props: {
        offer: FlightSearch.Itineraries;
        currency: Currency.CURRENCIES;
        criteria: Flight.SearchCriteria;
      }) => {
        track(Names.Flights.Redirect.Show, getOfferSelectData(props));
      };

      export const continueRedirect = (props: {
        offer: FlightSearch.Itineraries;
        currency: Currency.CURRENCIES;
        criteria: Flight.SearchCriteria;
      }) => {
        track(Names.Flights.Redirect.Continue, getOfferSelectData(props));
      };
    }

    function getLegPlaceByType(
      place: FlightSearch.Place,
      type: SkyscannerPlaceType,
    ) {
      if (place.type === type) {
        return place.name;
      } else if (place.parent) {
        return getLegPlaceByType(place.parent, type);
      } else {
        return '';
      }
    }

    function getOfferSelectData({
      offer,
      currency,
      criteria,
    }: {
      offer: FlightSearch.Itineraries;
      currency: Currency.CURRENCIES;
      criteria: Flight.SearchCriteria;
    }) {
      return {
        ...getTraits(),
        from: offer.outboundLeg.stations.origin.code,
        from_city: getLegPlaceByType(
          offer.outboundLeg.stations.origin,
          SkyscannerPlaceType.city,
        ),
        from_country: getLegPlaceByType(
          offer.outboundLeg.stations.origin,
          SkyscannerPlaceType.country,
        ),
        to: offer.outboundLeg.stations.destination.code,
        to_city: getLegPlaceByType(
          offer.outboundLeg.stations.destination,
          SkyscannerPlaceType.city,
        ),
        to_country: getLegPlaceByType(
          offer.outboundLeg.stations.destination,
          SkyscannerPlaceType.country,
        ),
        roundtrip: criteria.tripType === FlightCore.TripType.RoundTrip,
        departure_date: criteria.departDate
          ? format(criteria.departDate, 'YYYY-MM-DD')
          : null,
        return_date: criteria.returnDate
          ? format(criteria.returnDate, 'YYYY-MM-DD')
          : null,
        adults: criteria.adults,
        children: criteria.childrenAges,
        cabin_class: criteria.cabinClass,
        airline_iata_code: offer.outboundLeg.carriers[0].displayCode,
        airline_name: offer.outboundLeg.carriers[0].name,
        price: offer.prices[0].totalPrice,
        currency,
      };
    }
  }

  export namespace Typeform {
    export const skip = () => {
      track(Names.Integration.Typeform, { ...getTraits(), action: 'skip' });
    };

    export const start = () => {
      track(Names.Integration.Typeform, { ...getTraits(), action: 'start' });
    };
  }

  export namespace Payment {
    export namespace AddCard {
      export const start = () => {
        track(Names.Payment.AddCard.Start, getTraits());
      };

      export const finish = (args?: Object) => {
        const props = {
          ...getTraits(),
          ...args,
        };

        track(Names.Payment.AddCard.Finish, props);
      };
    }
  }

  //  export function signIn(body: Analytics.SignIn, startTime: number, error?: FPError ) {
  //     const props = {
  //       duration: (new Date().getTime() - startTime).millisecondsToSec(),
  //       error: error ? true : false,
  //       ...getTraits(),
  //     }
  //     if (body) {
  //       props['subscription_plan'] = body.subscription_plan;
  //       props['auth_type'] = body.auth_type;
  //       props['new_user'] = body.new_user;
  //     }
  //     if (error) {
  //       props['error_text'] = error.message;
  //       props['error_number'] = error.header;
  //       props['error_event_id'] = error.event_id;
  //       if (error.details)  props['error_details'] = error.details;
  //       pageError();
  //     }
  //     track(Names.Auth.Finish, props);
  //   };

  //   export function logout() {
  //     analytics.reset();
  //   }

  //plans

  // export function showPlans() {
  //   track(Names.Pla ns.Shown, getTraits())
  // }

  // export function planSelected({name, id}: { name: string, id: string} ) {
  //   const props = {
  //     ...getTraits(),
  //     subscription_plan: name,
  //     subscription_plan_id: id,
  //   }
  //   track(Names.Plans.Selected, props);
  // }

  //suggest

  // export function suggestStart(query: string) {
  //   const props = {
  //     ...getTraits(true),
  //     text: query ? query : '',
  //   };
  //   track(Names.Suggest.Start, props)
  // };

  // export function suggestFinish(text: string, total: number, cities: number, airports: number, hotels: number, geo, duration: number) {

  //   const props = {
  //     ...getTraits(true),
  //     total_destinations: total,
  //     text,
  //     cities,
  //     airports,
  //     hotels,
  //     regions: geo,
  //     duration: duration.millisecondsToSec(),
  //   };

  //   track(Names.Suggest.Finish, props);
  // }

  // export function suggestSelected<T extends Suggest.TypedSuggest>(
  //         suggest: Suggest.Suggest<T>,
  //         position: number) {
  //   const isCars = Suggest.instanceOfCar(suggest);
  //   if (isCars) {
  //     return;
  //   }
  //   const source = suggest.source as Suggest.HotelSuggest;
  //   const props = {
  //     ...getTraits(true),
  //     position,
  //     name: suggest ? suggest.title : '',
  //     city: suggest && source.geo ? source.geo.city : '',
  //     country: suggest && source.geo ? source.geo.country_title : '',
  //     state: suggest && source.geo ? source.geo.state : '',
  //     destination_type: suggest && source.type ? source.type : '',
  //   }

  //   track(Names.Suggest.Select, props);
  // }

  // //search page

  // export function searchStart(searchId, suggest: Suggest.Suggest<Suggest.HotelSuggest>, criteria: SearchCriteria) {
  //   const props = {
  //     ...getTraits(true),
  //     search_id: searchId,
  //     booking_window: Math.round((parse(criteria.checkin).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
  //     stay_nights: Math.round((parse(criteria.checkout).getTime() - parse(criteria.checkin).getTime()) / (1000 * 60 * 60 * 24)),
  //     checkin: criteria.checkin + '',
  //     checkout: criteria.checkout + '',
  //     adults: criteria.adults,
  //   }
  //   if (suggest) {
  //     props['destination_type'] = suggest.source.type ? suggest.source.type : '';
  //     props['destination'] = suggest.title ? suggest.title : '';
  //     if (suggest.source.geo) {
  //       props['country'] = suggest.source.geo.country ? suggest.source.geo.country : '';
  //       props['city'] = suggest.source.geo.city ? suggest.source.geo.city : '';
  //       props['state'] = suggest.source.geo.state ? suggest.source.geo.state : '';
  //     }
  //   }

  //   pageSearch();
  //   track(Names.Search.Start, props);

  // }

  // export function searchFinish(props: object) {
  //   props = {...props, ...getTraits(true)}
  //   track(Names.Search.Finish, props)
  // }

  // export function lowSavings(props: object) {
  //   analytics.ready(() => {
  //     props = {...props, ...getTraits(true)}
  //     analytics.track('h_web_search_low_savings', props);
  //   })
  // }

  // export function searchWrongPrice(props: object) {
  //   analytics.ready(() => {
  //     props = {...props, ...getTraits(true)}
  //     analytics.track('h_web_search_wrong_price', props);
  //   })
  // }

  // export function onHotelSelect(props: object) {
  //   props = {...props, ...getTraits(true)}
  //   track(Names.Search.Select, props);
  // }
  // //hotel page

  // export function selectRoom(body: any) {
  //   // window['analytics'].track('h_hotel_select_room', body);
  // };

  // export function getOfferStart(criteria: SearchCriteria, suggest: Suggest.Suggest<Suggest.HotelSuggest>) {
  //   pageHotel();
  //   const props = {
  //     ...getTraits(true),
  //     hotel_id: criteria.id,
  //     checkin: criteria.checkin+'',
  //     checkout: criteria.checkout+'',
  //     adults: criteria.adults,
  //     booking_window: criteria.getBookingWindow(),
  //     stay_nights: criteria.getNights(),
  //   }

  //   if (suggest) {
  //     props['destination_type'] = suggest.source.type ? suggest.source.type : '';
  //     props['destination'] = suggest.title ? suggest.title : '';
  //     if (suggest.source.geo) {
  //       props['country'] = suggest.source.geo.country ? suggest.source.geo.country : '';
  //       props['city'] = suggest.source.geo.city ? suggest.source.geo.city : '';
  //       props['state'] = suggest.source.geo.state ? suggest.source.geo.state : '';
  //     }
  //   }
  //   track(Names.Offers.Start, props);
  // };

  // export function getOfferFinish(criteria: SearchCriteria, hotel: Hotel.HotelResultV2, startTime: number, searchProps: Hotel.SearchProps) {

  //   const location = hotel.hotel['location'];
  //   const allOffers = ([] as Hotel.OfferV2[]).concat(...hotel.groups.map(it => it.offers));

  //   const props = {
  //     ...getTraits(true),
  //     request_id: hotel.request_id,
  //     hotel_id: criteria.id,
  //     hotel_name: hotel.hotel['title'],
  //     checkin: criteria.checkin+'',
  //     checkout: criteria.checkout+'',
  //     adults: criteria.adults,
  //     booking_window: criteria.getBookingWindow(),
  //     stay_nights: criteria.getNights(),
  //     rooms_category: hotel.groups.length,
  //     offers: allOffers.length,
  //     error: allOffers.length > 0 ? false : true,
  //     duration: (new Date().getTime() - startTime).millisecondsToSec(),
  //   }
  //   if (location) {
  //     props['city'] = location['city'];
  //     props['state'] = location['state'];
  //     props['country'] = location['country'];
  //   }

  //   const minPrice = (allOffers && allOffers.length > 0) ? allOffers.min(it => it.prices.disable_credits.price_with_extra_savings_per_night).prices.disable_credits.price_with_extra_savings_per_night : 0;

  //   if (allOffers && allOffers.length > 0) {
  //     props['price_min'] = minPrice;
  //     props['price_max'] = allOffers.max(it => it.prices.disable_credits.price_with_extra_savings_per_night).prices.disable_credits.price_with_extra_savings_per_night;
  //     props['savings_min'] = allOffers.min(it => it.prices.disable_credits.saving).prices.disable_credits.saving;
  //     props['savings_max'] = allOffers.max(it => it.prices.disable_credits.saving).prices.disable_credits.saving;
  //     const byPrice = allOffers.slice().sort((a, b) =>  a.prices.disable_credits.price_with_extra_savings_per_night - b.prices.disable_credits.price_with_extra_savings_per_night);
  //     const byTotal = allOffers.slice().sort((a, b) =>  a.prices.disable_credits.trip_total - b.prices.disable_credits.trip_total);

  //     let maxInGroup: Hotel.OfferV2 = null;
  //     let hasWrongRoom = false;

  //     const standart = hotel.groups.find(it => it.name.toLowerCase().includes('standard'));
  //     const standartMaxPriceOffer = standart ? standart.offers.max(it => it.prices.disable_credits.price_with_extra_savings_per_night) : null;
  //     const standartMaxPrice = standartMaxPriceOffer ? standartMaxPriceOffer.prices.disable_credits.price_with_extra_savings_per_night : 0;
  //     const checkPriceSorting = hotel.groups.slice().map(it => {
  //       const price = it.offers.slice().sort((a, b) => a.prices.disable_credits.price_with_extra_savings_per_night - b.prices.disable_credits.price_with_extra_savings_per_night);
  //       const total = it.offers.slice().sort((a, b) => a.prices.disable_credits.trip_total - b.prices.disable_credits.trip_total);
  //       if (!it.name.toLowerCase().includes('standard') && price.length > 0 && standartMaxPrice > price[0].prices.disable_credits.price_with_extra_savings_per_night) hasWrongRoom = true;
  //       const diff = price.reduce((acc,x,i) => { return acc && price[i].prices.disable_credits.token === total[i].prices.disable_credits.token }, true);
  //       return { rooms_category: it.name, hasIssue: !diff }
  //     });

  //     if (hasWrongRoom) {
  //       analytics.track('h_web_offer_wrong_room_type', {
  //         ...getTraits(),
  //         request_id: hotel.request_id,
  //         hotel_id: criteria.id,
  //         hotel_name: hotel.hotel['title'],
  //         checkin: criteria.checkin+'',
  //         checkout: criteria.checkout+'',
  //         adults: criteria.adults,
  //         city: location ? location['city'] : '',
  //         state: location ? location['state'] : '',
  //         country: location ? location['country'] : '',
  //         original_room_type: standartMaxPriceOffer ? standartMaxPriceOffer.name : '',
  //       })
  //     }

  //     if (checkPriceSorting.filter(it => it.hasIssue).length > 0) {
  //       analytics.track('h_web_offer_wrong_price', {
  //         ...getTraits(),
  //         request_id: hotel.request_id,
  //         hotel_id: criteria.id,
  //         hotel_name: hotel.hotel['title'],
  //         checkin: criteria.checkin+'',
  //         checkout: criteria.checkout+'',
  //         adults: criteria.adults,
  //         city: location ? location['city'] : '',
  //         state: location ? location['state'] : '',
  //         country: location ? location['country'] : '',
  //       });
  //     }
  //   }

  //   if (searchProps) {
  //     const searchMinPrice = +searchProps.minPrice;
  //     const priceDiff: number = searchMinPrice > 0 ? minPrice - searchMinPrice : 0;
  //     if (Math.abs(priceDiff) > 0  || searchMinPrice === 0) {
  //       analytics.track('h_web_search_price_do_not_match', {
  //         ...getTraits(),
  //         request_id: hotel.request_id,
  //         hotel_id: criteria.id,
  //         hotel_name: hotel.hotel['title'],
  //         checkin: criteria.checkin+'',
  //         checkout: criteria.checkout+'',
  //         adults: criteria.adults,
  //         city: location ? location['city'] : '',
  //         state: location ? location['state'] : '',
  //         country: location ? location['country'] : '',
  //         price_diff: priceDiff,
  //         search_in_process: searchProps.searchFinish === 'true' ? false : true,
  //         price_on_search: searchMinPrice,
  //         price_on_offers_request: minPrice,
  //       });
  //     }
  //   }
  //   track(Names.Offers.Finish, props);
  // }

  // export function onGetOfferFinishWithError(criteria: SearchCriteria, error: FPError, startTime: number) {

  //   const props = {
  //     ...getTraits(true),
  //     hotel_id: criteria.id,
  //     checkin: criteria.checkin+'',
  //     checkout: criteria.checkout+'',
  //     adults: criteria.adults,
  //     booking_window: criteria.getBookingWindow(),
  //     stay_nights: criteria.getNights(),
  //     error: true,
  //     error_text: error.message,
  //     error_number: error.header,
  //     error_event_id: error.event_id,
  //     duration: (new Date().getTime() - startTime).millisecondsToSec(),
  //   }
  //   pageError();
  //   if (error.details) props['error_details'] = error.details;
  //   track(Names.Offers.Finish, props);

  // }

  // export function getPolicyStart(offer: Hotel.OfferV2, hotel: Hotel.HotelDetailsV2) {
  //   const props = {
  //     user: getTraits(true),
  //     hotel_id: hotel.id,
  //     hotel_name: hotel.title,
  //     room_group: offer.group_name,
  //     free_cancelation: offer.refundable,
  //     eligible_for_points: offer.options.some(it => it.id === Hotel.OPTION_STATIC_ID.ELIGIBLE_FOR_POINTS),
  //     pay_now: offer.prepay,
  //     token: offer.prices.disable_credits.token,
  //     supplier: offer.supplier,
  //   }
  //   if (hotel.location) {
  //     if (hotel.location.city) props['city'] = hotel.location.city;
  //     if (hotel.location.state) props['state'] = hotel.location.state;
  //     if (hotel.location.country) props['country'] = hotel.location.country;
  //   }
  //   track(Names.Policy.Start, props);
  // }

  // export function getPolicyFinish(offer: Hotel.OfferV2, hotel: Hotel.HotelDetailsV2, startTime, error?: FPError) {

  //   const props = {
  //     ...getTraits(true),
  //     hotel_id: hotel.id,
  //     hotel_name: hotel.title,
  //     room_group: offer.group_name,
  //     free_cancelation: offer.refundable,
  //     eligible_for_points: offer.options.some(it => it.id === Hotel.OPTION_STATIC_ID.ELIGIBLE_FOR_POINTS),
  //     pay_now: offer.prepay,
  //     token: offer.prices.disable_credits.token,
  //     supplier: offer.supplier,
  //     error: error ? true : false,
  //     duration: (new Date().getTime() - startTime).millisecondsToSec(),
  //   }

  //   if (hotel.location) {
  //     if (hotel.location.city) props['city'] = hotel.location.city;
  //     if (hotel.location.state) props['state'] = hotel.location.state;
  //     if (hotel.location.country) props['country'] = hotel.location.country;
  //   }
  //   if (error) {
  //     props['error_text'] = error.message;
  //     props['error_number'] = error.header;
  //     props['error_event_id'] = error.event_id;
  //     if (error.details)  props['error_details'] = error.details;
  //     pageError();
  //   } else {
  //     pageBook();
  //   }

  //   track(Names.Policy.Finish, props);
  // }

  // export function viewHotelGallery(ids: Analytics.Identifiers, hotelId, images: string[]) {
  //   // const body = {
  //   //   use_case_id: ids.useCaseId,
  //   //   offers_id: ids.offerId,
  //   //   hotel_id: hotelId,
  //   //   images_count: images ? images.length : 0,
  //   //   photo_urls: images,
  //   // }
  //   // window['analytics'].track('h_hotel_view_hotel_gallery');
  // }

  // //booking
  // export function bookingStart(hotel: Hotel.HotelDetailsV2, offer: Hotel.OfferV2, criteria: SearchCriteria) {
  //   const price: Hotel.Credits = offer.prices.enable_credits && useCredits ? offer.prices.enable_credits : offer.prices.disable_credits;
  //   const props = {
  //     ...getTraits(),
  //     token: price.token,
  //     hotel_id: hotel.id,
  //     hotel_name: hotel.title,
  //     room_group: offer.group_name,
  //     eligible_for_points: offer.options.some(it => it.id === Hotel.OPTION_STATIC_ID.ELIGIBLE_FOR_POINTS),
  //     pay_now: offer.prepay,
  //     free_cancelation: offer.refundable,
  //     checkin: criteria.checkin+'',
  //     checkout: criteria.checkout+'',
  //     adults: criteria.adults,
  //     booking_window: criteria.getBookingWindow(),
  //     stay_nights: criteria.getNights(),
  //     supplier: offer.supplier,
  //     price_public: price.public_price_per_night,
  //     price_public_total: price.public_price,
  //     taxes: price.taxes,
  //     fees: price.supplements_and_taxes - price.taxes,
  //     savings_total: price.saving,
  //     savings_type: price.discount > 0 ? 'discount' : 'cashback',
  //   };

  //   if (hotel.location) {
  //     if (hotel.location.city) props['city'] = hotel.location.city;
  //     if (hotel.location.state) props['state'] = hotel.location.state;
  //     if (hotel.location.country) props['country'] = hotel.location.country;
  //   }
  //   track(Names.Booking.Start, props);
  // }

  // export function bookingFinish(startTime, criteria: SearchCriteria, offer: Hotel.OfferV2, hotel: Hotel.HotelDetailsV2, useCredits: boolean, res?: Order.OrderResponse, error?: FPError) {
  //   const price = offer.prices.disable_credits;
  //   const props = {
  //     ...getTraits(),
  //     token: price.token,
  //     hotel_id: hotel.id,
  //     hotel_name: hotel.title,
  //     room_group: offer.group_name,
  //     eligible_for_points: offer.options.some(it => it.id === Hotel.OPTION_STATIC_ID.ELIGIBLE_FOR_POINTS),
  //     pay_now: offer.prepay,
  //     free_cancelation: offer.refundable,
  //     checkin: criteria.checkin+'',
  //     checkout: criteria.checkout+'',
  //     adults: criteria.adults,
  //     booking_window: criteria.getBookingWindow(),
  //     stay_nights: criteria.getNights(),
  //     supplier: offer.supplier,
  //     price_public: price.public_price_per_night,
  //     price_public_total: price.public_price,
  //     taxes: price.taxes,
  //     fees: price.supplements_and_taxes - price.taxes,
  //     savings_total: price.saving,
  //     savings_type: price.discount > 0 ? 'discount' : 'cashback',
  //     duration: (new Date().getTime() - startTime).millisecondsToSec(),
  //     booking_supplier: res ? res.supplier : '',
  //     booking_confirmation: res ? res.order_number : '',
  //     booking_status: res ? res.order_status : '',
  //   };

  //   if (res) {
  //     props['booking_confirmation'] = res.order_number;
  //     props['booking_status'] = res.status;
  //   }

  //   if (hotel.location) {
  //     if (hotel.location.city) props['city'] = hotel.location.city;
  //     if (hotel.location.state) props['state'] = hotel.location.state;
  //     if (hotel.location.country) props['country'] = hotel.location.country;
  //   }

  //   if (error) {
  //     props['error_text'] = error.message;
  //     props['error_number'] = error.header;
  //     props['error_event_id'] = error.event_id;
  //     if (error.details)  props['error_details'] = error.details;

  //     pageError();
  //     track(Names.Booking.Error, props)
  //   } else {
  //     pageThanks();
  //     track(Names.Booking.Finish, props)
  //   }
  // }

  // export function addNewTraveler() {
  //   track(Names.Guest.Add, getTraits());
  //   // analytics.ready(() => {
  //   //   analytics.track('h_web_add_new_traveler');
  //   // });
  // }

  // export function selectTraveler() {
  //   track(Names.Guest.Select, getTraits());
  // }

  // export function whoIsStayingCompleted() {
  //   analytics.ready(() => {
  //     analytics.track('h_web_whoistaying_completed');
  //   });
  // }

  // export function addNewCard() {
  //   track(Names.Card.Add, getTraits());
  //   // analytics.ready(() => {
  //   //   analytics.track('h_web_add_new_cards');
  //   // });
  // }

  // export function selectCard() {
  //   track(Names.Card.Select, getTraits() );
  // }

  // export function paymentCompleted() {
  //   analytics.ready(() => {
  //     analytics.track('h_web_payment_method_completed');
  //   });
  // }

  // export function pageLanded() {
  //   track(Names.Page.Landed, getTraits(), true);
  // }

  // //page tracking

  // export function pageBook() {
  //   analytics.ready(()=> {
  //     analytics.page('Book', {
  //       title: 'Book hotel',
  //       path: '/book',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageMain() {
  //   analytics.ready(() => {
  //     analytics.page('Main', {
  //       title: 'Main page',
  //       path: '/main',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageSearch() {
  //   analytics.ready(() => {
  //     analytics.page('Search', {
  //       title: 'Search page',
  //       path: '/search',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageHotel() {
  //   analytics.ready(() => {
  //     analytics.page('Hotel', {
  //       title: 'Hotel page',
  //       path: '/hotel',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageThanks() {
  //   analytics.ready(() => {
  //     analytics.page('Thanks', {
  //       title: 'Thanks page',
  //       path: '/Thanks',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageError() {
  //   analytics.ready(() => {
  //     analytics.page('Error', {
  //       title: 'Error page',
  //       path: '/error',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageSignin() {
  //   analytics.ready(() => {
  //     analytics.page('SignIn', {
  //       title: 'SignIn page',
  //       path: '/signin',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }

  // export function pageSignup() {
  //   analytics.ready(() => {
  //     analytics.page('SignUp', {
  //       title: 'SignUp page',
  //       path: '/signup',
  //       search: '',
  //       url: window.location.host,
  //     });
  //   });
  // }
}
